/* Localize verified Judge.me product review-form labels without replacing its behavior. */
if (!customElements.get('lala-review-form')) {
  const reviewFormLabels = {
    de: {
      'Close review form': 'Bewertung schliessen',
      'Review form pages': 'Seiten des Bewertungsformulars',
      'Review form navigation': 'Navigation im Bewertungsformular',
      'Previous step': 'Vorheriger Schritt',
      'Next step': 'Nächster Schritt',
      Rating: 'Bewertung',
      'Privacy and Terms': 'Datenschutz und Geschäftsbedingungen'
    },
    en: {
      'Close review form': 'Close review form',
      'Review form pages': 'Review form pages',
      'Review form navigation': 'Review form navigation',
      'Previous step': 'Previous step',
      'Next step': 'Next step',
      Rating: 'Rating',
      'Privacy and Terms': 'Privacy and Terms'
    },
    fr: {
      'Close review form': 'Fermer l’avis',
      'Review form pages': 'Pages du formulaire d’avis',
      'Review form navigation': 'Navigation du formulaire d’avis',
      'Previous step': 'Étape précédente',
      'Next step': 'Étape suivante',
      Rating: 'Note',
      'Privacy and Terms': 'Confidentialité et conditions'
    },
    it: {
      'Close review form': 'Chiudi la recensione',
      'Review form pages': 'Pagine del modulo di recensione',
      'Review form navigation': 'Navigazione del modulo di recensione',
      'Previous step': 'Passaggio precedente',
      'Next step': 'Passaggio successivo',
      Rating: 'Valutazione',
      'Privacy and Terms': 'Privacy e condizioni'
    }
  };

  const starPattern = /^([1-5]) (star|stars)$/;
  const modalSelector = '.jdgm-write-review-modal[role="dialog"][aria-modal="true"]';

  class LalaReviewForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const locale = (this.getAttribute('lang') || document.documentElement.lang || 'en').split('-')[0];
      this.locale = reviewFormLabels[locale] ? locale : 'en';
      this.labels = reviewFormLabels[this.locale];
      this.ownedAttributes = new WeakMap();
      this.ownedElements = new Set();
      this.closeRepairs = new WeakMap();
      this.currentPage = null;
      this.focusToken = 0;
      this.localizationFrame = 0;
      this.onBodyMutations = this.onBodyMutations.bind(this);
      this.onModalMutations = this.onModalMutations.bind(this);
      this.onScoreSignal = this.onScoreSignal.bind(this);
      this.bodyObserver = new MutationObserver(this.onBodyMutations);
      this.modalObserver = new MutationObserver(this.onModalMutations);
      this.bodyObserver.observe(document.body, { childList: true, subtree: true });
      this.syncModal();
    }

    disconnectedCallback() {
      this.bodyObserver?.disconnect();
      this.modalObserver?.disconnect();
      cancelAnimationFrame(this.localizationFrame);
      this.localizationFrame = 0;
      this.focusToken += 1;
      this.releaseModal();
      this.initialized = false;
    }

    setOwnedAttribute(element, attribute, source, replacement) {
      const current = element.getAttribute(attribute);
      const attributes = this.ownedAttributes.get(element) || {};
      let owned = attributes[attribute];
      if (!owned) {
        if (current !== source && current !== replacement) return false;
        owned = { source, rendered: current };
        attributes[attribute] = owned;
        this.ownedAttributes.set(element, attributes);
        this.ownedElements.add(element);
      } else if (current !== owned.source && current !== owned.rendered) {
        delete attributes[attribute];
        return false;
      }
      if (current !== replacement) element.setAttribute(attribute, replacement);
      owned.rendered = replacement;
      return true;
    }

    localizeKnownLabel(element, attribute) {
      const attributes = this.ownedAttributes.get(element);
      const ownedSource = attributes?.[attribute]?.source;
      const current = element.getAttribute(attribute);
      const localizedSource = Object.entries(this.labels).find(([, value]) => value === current)?.[0];
      const source = ownedSource || (Object.hasOwn(this.labels, current) ? current : localizedSource || null);
      if (!source || !Object.hasOwn(this.labels, source)) return false;
      return this.setOwnedAttribute(element, attribute, source, this.labels[source]);
    }

    starLabel(label) {
      const match = label?.match(starPattern);
      if (!match) return undefined;
      const value = Number(match[1]);
      if (this.locale === 'de') return `${value} ${value === 1 ? 'Stern' : 'Sterne'}`;
      if (this.locale === 'fr') return `${value} ${value === 1 ? 'étoile' : 'étoiles'}`;
      if (this.locale === 'it') return `${value} ${value === 1 ? 'stella' : 'stelle'}`;
      return `${value} ${value === 1 ? 'star' : 'stars'}`;
    }

    ratingSelectionLabel(value) {
      if (!value) return this.labels.Rating;
      if (this.locale === 'de') return `Bewertung: ${value} von 5 Sternen ausgewählt`;
      if (this.locale === 'fr') return `Note : ${value} ${value === 1 ? 'étoile sur 5 sélectionnée' : 'étoiles sur 5 sélectionnées'}`;
      if (this.locale === 'it') return `Valutazione: ${value} ${value === 1 ? 'stella su 5 selezionata' : 'stelle su 5 selezionate'}`;
      return `Rating: ${value} out of 5 stars selected`;
    }

    selectedStarLabel(label, selected) {
      const localized = this.starLabel(label);
      if (!localized || !selected) return localized;
      if (this.locale === 'de') return `${localized}, ausgewählt`;
      if (this.locale === 'fr') return `${localized}, ${label.startsWith('1 ') ? 'sélectionnée' : 'sélectionnées'}`;
      if (this.locale === 'it') return `${localized}, ${label.startsWith('1 ') ? 'selezionata' : 'selezionate'}`;
      return `${localized}, selected`;
    }

    localizeStar(star, selectedValue) {
      for (const attribute of ['aria-label', 'title']) {
        const attributes = this.ownedAttributes.get(star);
        const ownedSource = attributes?.[attribute]?.source;
        const current = star.getAttribute(attribute);
        if (attribute === 'aria-label' && ownedSource && current === this.starLabel(ownedSource)) {
          attributes[attribute].rendered = current;
        }
        let source = ownedSource || (starPattern.test(current || '') ? current : null);
        if (!source && attribute === 'aria-label') {
          source = ['1 star', '2 stars', '3 stars', '4 stars', '5 stars']
            .find(candidate => this.starLabel(candidate) === current ||
              this.selectedStarLabel(candidate, true) === current) || null;
        } else if (!source && attribute === 'title') {
          source = ['1 star', '2 stars', '3 stars', '4 stars', '5 stars']
            .find(candidate => this.starLabel(candidate) === current) || null;
        }
        if (!source) continue;
        const value = source.match(starPattern)?.[1];
        const selected = attribute === 'aria-label' && value === selectedValue;
        if (!ownedSource && attribute === 'aria-label' && current === this.starLabel(source)) {
          this.setOwnedAttribute(star, attribute, source, this.starLabel(source));
        }
        this.setOwnedAttribute(star, attribute, source, this.selectedStarLabel(source, selected));
      }
    }

    localizeRatingGroup(group) {
      const score = group.querySelector('input[name="score"]')?.value;
      const selectedValue = /^[1-5]$/.test(score || '') ? score : '';
      const attributes = this.ownedAttributes.get(group);
      const source = attributes?.['aria-label']?.source ||
        (['Rating', this.labels.Rating, this.ratingSelectionLabel(selectedValue)]
          .includes(group.getAttribute('aria-label')) ? 'Rating' : null);
      if (source) {
        if (!attributes?.['aria-label'] && group.getAttribute('aria-label') === this.labels.Rating) {
          this.setOwnedAttribute(group, 'aria-label', source, this.labels.Rating);
        }
        this.setOwnedAttribute(group, 'aria-label', source, this.ratingSelectionLabel(selectedValue));
      }
      group.querySelectorAll('.jdgm-star').forEach(star => this.localizeStar(star, selectedValue));
    }

    restoreCloseButton(close = this.modal?.querySelector('.jdgm-write-review-modal__close-btn')) {
      const repaired = this.closeRepairs.get(close);
      if (!close || !repaired) return;
      if (repaired.ariaHidden && !close.hasAttribute('aria-hidden')) close.setAttribute('aria-hidden', 'true');
      if (repaired.tabindex && !close.hasAttribute('tabindex')) close.setAttribute('tabindex', '-1');
      this.closeRepairs.delete(close);
    }

    restoreOwnedAttributes() {
      for (const element of this.ownedElements) {
        const attributes = this.ownedAttributes.get(element) || {};
        for (const [attribute, owned] of Object.entries(attributes)) {
          if (element.getAttribute(attribute) === owned.rendered) {
            element.setAttribute(attribute, owned.source);
          }
        }
      }
    }

    syncEditableClose(currentPage) {
      const close = this.modal.querySelector('.jdgm-write-review-modal__close-btn');
      const editable = Boolean(currentPage &&
        !currentPage.classList.contains('jdgm-write-review-modal__page--thank-you'));
      if (editable && !this.modal.hasAttribute('data-lala-review-form-editable')) {
        this.modal.setAttribute('data-lala-review-form-editable', '');
      } else if (!editable && this.modal.hasAttribute('data-lala-review-form-editable')) {
        this.modal.removeAttribute('data-lala-review-form-editable');
      }
      const owned = this.ownedAttributes.get(close)?.['aria-label']?.source === 'Close review form';
      if (!close || !owned || !editable) {
        this.restoreCloseButton(close);
        return;
      }
      const repaired = this.closeRepairs.get(close) || {};
      if (close.getAttribute('aria-hidden') === 'true') {
        repaired.ariaHidden = true;
        close.removeAttribute('aria-hidden');
      }
      if (close.getAttribute('tabindex') === '-1') {
        repaired.tabindex = true;
        close.removeAttribute('tabindex');
      }
      this.closeRepairs.set(close, repaired);
    }

    localizeModal() {
      if (!this.modal?.isConnected) return;
      this.modal.querySelectorAll('[aria-label]').forEach(element => {
        if (!element.matches('.jdgm-star') && !element.matches('.jdgm-write-review-modal__stars[role="group"]')) {
          this.localizeKnownLabel(element, 'aria-label');
        }
      });
      this.modal.querySelectorAll('.jdgm-write-review-modal__stars[role="group"]')
        .forEach(group => this.localizeRatingGroup(group));
      const currentPage = this.modal.querySelector('.jdgm-write-review-modal__page.current-page');
      this.syncEditableClose(currentPage);
      this.syncCurrentPage(currentPage);
    }

    releaseModal() {
      if (this.modal) {
        this.restoreCloseButton();
        for (const type of ['input', 'change', 'click', 'keyup']) {
          this.modal.removeEventListener(type, this.onScoreSignal, true);
        }
        this.restoreOwnedAttributes();
      }
      this.modal?.removeAttribute('data-lala-review-form');
      this.modal?.removeAttribute('data-lala-review-form-editable');
      this.modal = null;
      this.pendingModal = null;
      this.currentPage = null;
      this.ownedAttributes = new WeakMap();
      this.ownedElements = new Set();
      this.closeRepairs = new WeakMap();
      this.focusToken += 1;
    }

    syncModal() {
      const allCandidates = [...document.querySelectorAll(modalSelector)];
      const candidates = allCandidates.filter(modal =>
        modal.isConnected && modal.getClientRects().length &&
        getComputedStyle(modal).visibility !== 'hidden' && getComputedStyle(modal).display !== 'none'
      );
      const nextModal = candidates[candidates.length - 1] || null;
      if (nextModal && nextModal === this.modal) {
        this.localizeModal();
        return;
      }
      this.modalObserver.disconnect();
      this.releaseModal();
      if (!nextModal) {
        this.pendingModal = allCandidates[allCandidates.length - 1] || null;
        if (this.pendingModal) {
          this.modalObserver.observe(this.pendingModal, {
            attributes: true,
            attributeFilter: ['style', 'class', 'aria-hidden']
          });
        }
        return;
      }
      this.modal = nextModal;
      this.modal.setAttribute('data-lala-review-form', '');
      this.localizeModal();
      for (const type of ['input', 'change', 'click', 'keyup']) {
        this.modal.addEventListener(type, this.onScoreSignal, { capture: true, passive: true });
      }
      this.modalObserver.observe(this.modal, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-label', 'title', 'class', 'style', 'aria-hidden', 'tabindex']
      });
    }

    mutationContainsModal(mutation) {
      return [...mutation.addedNodes, ...mutation.removedNodes].some(node =>
        node?.nodeType === 1 && (node.matches?.(modalSelector) || node.querySelector?.(modalSelector))
      );
    }

    onBodyMutations(mutations) {
      if (mutations.some(mutation => this.mutationContainsModal(mutation))) this.syncModal();
    }

    onModalMutations() {
      this.scheduleLocalization();
    }

    onScoreSignal(event) {
      if (event.target?.closest?.('.jdgm-write-review-modal__stars')) this.scheduleLocalization();
    }

    scheduleLocalization() {
      if (this.localizationFrame) return;
      this.localizationFrame = requestAnimationFrame(() => {
        this.localizationFrame = 0;
        this.syncModal();
      });
    }

    syncCurrentPage(currentPage) {
      const page = currentPage?.getAttribute('data-page');
      if (!this.currentPage) {
        if (page) this.currentPage = page;
        return;
      }
      if (!page || page === this.currentPage) return;
      this.currentPage = page;
      this.schedulePageFocus(page);
    }

    schedulePageFocus(page) {
      const modal = this.modal;
      const currentPage = modal?.querySelector('.jdgm-write-review-modal__page.current-page');
      if (!currentPage || currentPage.classList.contains('jdgm-write-review-modal__page--thank-you')) return;
      const token = ++this.focusToken;
      const settle = () => {
        if (token !== this.focusToken || modal !== this.modal) return;
        const animations = modal.getAnimations?.({ subtree: true })
          .filter(animation => animation.playState === 'running') || [];
        if (animations.length) {
          Promise.allSettled(animations.map(animation => animation.finished)).then(() => {
            requestAnimationFrame(() => this.repairPageFocus(modal, page, token));
          });
          return;
        }
        requestAnimationFrame(() => this.repairPageFocus(modal, page, token));
      };
      requestAnimationFrame(settle);
    }

    repairPageFocus(modal, page, token) {
      if (token !== this.focusToken || modal !== this.modal || !modal.isConnected ||
          modal.querySelector('.jdgm-write-review-modal__page.current-page')?.getAttribute('data-page') !== page ||
          !modal.getClientRects().length ||
          getComputedStyle(modal).visibility === 'hidden' || getComputedStyle(modal).display === 'none') return;
      const active = document.activeElement;
      const previousPage = active?.closest?.('.jdgm-write-review-modal__page');
      if (active !== document.body && !(previousPage && modal.contains(previousPage) &&
          !previousPage.classList.contains('current-page'))) return;
      const current = [...modal.querySelectorAll('.jdgm-write-review-modal__page.current-page')]
        .find(candidate => candidate.getAttribute('data-page') === page);
      const target = current?.querySelector('[aria-invalid="true"]') ||
        current?.querySelector('textarea:not([disabled])') ||
        current?.querySelector('input:not([type="hidden"]):not([disabled])') ||
        current?.querySelector('button:not([disabled])');
      target?.focus({ preventScroll: true });
    }
  }

  customElements.define('lala-review-form', LalaReviewForm);
}
